import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import type { SessionUser } from '@/api/profile/types';
import { version } from '../../../package.json';

const { VITE_RETRACED_ENV } = import.meta.env;

/** Environments (VITE_RETRACED_ENV values) that report to Datadog RUM. */
const RUM_ENABLED_ENVS = new Set([
  'demo',
  'team-product-compliance',
  'team-supply-chain-network-management',
  'staging',
  'production',
]);

const isRumEnabled = RUM_ENABLED_ENVS.has(VITE_RETRACED_ENV ?? '');

/**
 * Stream the fetch response body and attach the parsed JSON to the RUM event's
 * context so failed requests carry their payload into Datadog.
 *
 * Runs as fire-and-forget: `beforeSend` must return synchronously, but Datadog
 * batches events before flushing, so the mutation lands on `event.context`
 * before the event is serialised in the common case.
 */
async function attachResponseBody(
  body: ReadableStream<Uint8Array>,
  event: RumEvent,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let accumulatedData = '';
  const readChunk = async (): Promise<void> => {
    const { value, done } = await reader.read();
    if (done) return;
    accumulatedData += decoder.decode(value);
    await readChunk();
  };
  await readChunk();
  event.context = event.context ?? {};
  try {
    event.context.responseBody = JSON.parse(accumulatedData);
  } catch {
    event.context.responseBody = {};
  }
}

/**
 * Initialise Datadog RUM. No-op unless `VITE_RETRACED_ENV` is one of the
 * reporting environments (see `RUM_ENABLED_ENVS`) — local development and any
 * other env stay silent. Call once at app startup before rendering.
 */
export function initDatadogRum(): void {
  if (!isRumEnabled) return;

  datadogRum.init({
    // Trace all requests to retraced.com except auth.retraced.com.
    allowedTracingUrls: [/https:\/\/(?!auth\.).*\.retraced\.com.+/],
    applicationId: '368ee548-8292-40b1-a6a8-860e15a4daa8',
    clientToken: 'puba4918de5b8d3d4d902d9d9a99d5e3980',
    site: 'datadoghq.eu',
    service: 'dashboard-web-v2',
    env: VITE_RETRACED_ENV,
    version: `v${version}`,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    enableExperimentalFeatures: ['feature_flags'],
    beforeSend: (event, context) => {
      if (event.type === 'resource' && event.resource.type === 'fetch') {
        const { response } = context as { response?: Response };
        const body = response?.body;
        if (body && !body.locked) void attachResponseBody(body, event);
      }
      return true;
    },
  });
}

/**
 * Attach the authenticated user to the current RUM session. No-op when RUM is
 * not initialised (local development). Safe to call on every session change.
 */
export function setDatadogUser(user: SessionUser): void {
  if (!isRumEnabled) return;

  datadogRum.setUser({
    id: user.id,
    email: user.email,
    companyId: user.companyId,
  });
}
